import { Component, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Video, VideoCategory } from '../../../../core/models/video.model';
import { VideosService } from '../../../../core/services/videos.service';
import { PapersService, Paper, CreatePaperDto } from '../../../../core/services/papers.service';
import { UsersService } from '../../../../core/services/users.service';
import { ConfigurationService } from '../../../../core/services/configuration.service';
import { TestsService } from '../../../../core/services/tests.service';
import { User } from '../../../../core/models/user.interface';
import { BaseResponse } from '../../../../core/models/base.response';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { environment } from '../../../../../environments/environment';

// Interfaces para Papers
interface PaperCategory {
  id: string;
  name: string;
}

// Interfaces para Academy Categories
interface AcademyCategory {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  createdAt: Date;
}

// Interfaces para Expert Categories
interface ExpertCategory {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  createdAt: Date;
}

@Component({
  selector: 'app-admin-videos',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxSpinnerModule],
  templateUrl: './admin-videos.component.html',
  styleUrls: ['./admin-videos.component.scss']
})
export class AdminVideosComponent implements OnInit {
  private sanitizer = inject(DomSanitizer);
  private videosService = inject(VideosService);
  private papersService = inject(PapersService);
  private usersService = inject(UsersService);
  private configurationService = inject(ConfigurationService);
  private spinner = inject(NgxSpinnerService);
  private testsService = inject(TestsService);

  // Propiedades de pestañas
  activeTab: 'videos' | 'papers' | 'academy-categories' | 'expert-categories' = 'videos';

  // Propiedades de la tabla de videos
  videos: Video[] = [];
  filteredVideos: Video[] = [];
  searchTerm: string = '';
  selectedCategory: string = 'all';
  currentPage: number = 1;
  pageSize: number = 10;
  
  // Propiedades para el formulario de video
  showVideoForm: boolean = false;
  editMode: boolean = false;
  currentVideoId: string | null = null;
  videoForm: {
    title: string;
    description: string;
    thumbnailUrl?: string;
    videoUrl: string;
    category: string;
    featured?: boolean;
    price?: number;
    isFree?: boolean;
    duration?: string;
    views?: number;
  } = {
    title: '',
    description: '',
    thumbnailUrl: '',
    videoUrl: '',
    category: '',
    featured: false,
    price: 0,
    isFree: false,
    duration: '',
    views: 0
  };
  
  // Propiedades para el preview de video
  previewVideoUrl: SafeResourceUrl | null = null;
  showVideoPreview: boolean = false;
  
  // Categorías de videos
  videoCategories: VideoCategory[] = [];

  // Propiedades de la tabla de papers
  papers: Paper[] = [];
  
  // Lista de expertos y mentores activos para selección de autor
  activeExperts: User[] = [];
  filteredPapers: Paper[] = [];
  paperSearchTerm: string = '';
  selectedPaperCategory: string = 'all';
  selectedPaperStatus: string = 'all';
  currentPaperPage: number = 1;
  paperPageSize: number = 10;

  // Propiedades para el formulario de paper
  showPaperForm: boolean = false;
  editPaperMode: boolean = false;
  currentPaperId: string | null = null;
  paperForm: {
    title: string;
    description: string;
    author: string; // ID del experto seleccionado
    category: string; // Mantener para compatibilidad con backend (primera categoría o todas separadas por comas)
    categories: string[]; // Array de IDs de categorías seleccionadas
    coverUrl: string;
    fileUrl: string;
    pages: number;
    price: number;
    isFree: boolean;
    featured: boolean;
    tags: string;
    learningObjectives: string;
    publishedDate: string | Date;
    downloads: number;
    content: string;
    summary: string;
    keywords: string;
    language: string;
    isbn: string;
    publisher: string;
    year: number;
    status: number; // 1 = activo, 2 = inactivo, 3 = En revision, 4 = Borrador
  } = {
    title: '',
    description: '',
    author: '',
    category: '',
    categories: [],
    coverUrl: '',
    fileUrl: '',
    pages: 0,
    price: 0,
    isFree: false,
    featured: false,
    tags: '',
    learningObjectives: '',
    publishedDate: '',
    downloads: 0,
    content: '',
    summary: '',
    keywords: '',
    language: 'es',
    isbn: '',
    publisher: '',
    year: new Date().getFullYear(),
    status: 4 // Por defecto: Borrador
  };
  
  // Propiedades para el multiselect de categorías
  categoryDropdownOpen: boolean = false;
  categoryError: string = '';
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const multiselectContainer = target.closest('.multiselect-container');
    if (!multiselectContainer && this.categoryDropdownOpen) {
      this.categoryDropdownOpen = false;
    }
  }

  // Propiedades para Academy Categories
  academyCategories: AcademyCategory[] = [];
  filteredAcademyCategories: AcademyCategory[] = [];
  academyCategorySearchTerm: string = '';
  showAcademyCategoryForm: boolean = false;
  editAcademyCategoryMode: boolean = false;
  academyCategoryForm: {
    id?: string;
    name: string;
    description?: string;
    status: 'active' | 'inactive';
  } = {
    name: '',
    description: '',
    status: 'active'
  };

  // Propiedades para Expert Categories
  expertCategories: ExpertCategory[] = [];
  filteredExpertCategories: ExpertCategory[] = [];
  expertCategorySearchTerm: string = '';
  showExpertCategoryForm: boolean = false;
  editExpertCategoryMode: boolean = false;
  expertCategoryForm: {
    id?: string;
    name: string;
    description?: string;
    status: 'active' | 'inactive';
  } = {
    name: '',
    description: '',
    status: 'active'
  };

  // Propiedades para el preview de paper
  previewPaper: Paper | null = null;
  showPaperPreview: boolean = false;

  // Propiedades para archivos seleccionados
  selectedPdfFile: File | null = null;
  selectedCoverFile: File | null = null;

  // Categorías de papers
  paperCategories: PaperCategory[] = [];

  constructor() {}

  ngOnInit(): void {
    this.loadPaperCategories();
    this.loadCategories();
    this.loadVideos();
    this.loadPapers();
    this.loadActiveExperts();
  }

  // Métodos de pestañas
  setActiveTab(tab: 'videos' | 'papers' | 'academy-categories' | 'expert-categories'): void {
    this.activeTab = tab;
    if (tab === 'academy-categories') {
      this.loadAcademyCategories();
    } else if (tab === 'expert-categories') {
      this.loadExpertCategories();
    }
  }

  // Métodos de videos (mantener los existentes)
  loadPaperCategories(): void {
    this.testsService.getDimensions().subscribe({
      next: (response) => {
        if (response && response.data) {
          this.paperCategories = response.data.map((dimension: any) => ({
            id: dimension.id,
            name: dimension.name
          }));
          console.log('Dimensiones para papers:', this.paperCategories);
        } else {
          this.paperCategories = [];
        }
      },
      error: (err) => {
        console.error('Error al obtener dimensiones para papers:', err);
        this.paperCategories = [];
      }
    });
  }

  loadCategories(): void {
    this.spinner.show();
    this.videosService.getVideoCategories().subscribe({
      next: (categories: VideoCategory[]) => {
        this.videoCategories = categories;
        this.spinner.hide();
      },
      error: (err: any) => {
        console.error('Error al cargar categorías', err);
        this.spinner.hide();
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar las categorías de videos',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  loadVideos(): void {
    this.spinner.show();
    this.videosService.getVideos().subscribe({
      next: (videos: Video[]) => {
        console.log('Videos cargados:', videos);
        this.videos = videos;
        this.applyFilters();
        this.spinner.hide();
      },
      error: (err: any) => {
        console.error('Error al cargar videos', err);
        this.spinner.hide();
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los videos',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  applyFilters(): void {
    this.filteredVideos = this.videos.filter(video => {
      const matchesSearch = video.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                           video.description.toLowerCase().includes(this.searchTerm.toLowerCase());
      const matchesCategory = this.selectedCategory === 'all' || video.category === this.selectedCategory;
      return matchesSearch && matchesCategory;
    });
    this.currentPage = 1;
  }

  getPageVideos(): Video[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredVideos.slice(startIndex, endIndex);
  }

  getTotalPages(): number {
    return Math.ceil(this.filteredVideos.length / this.pageSize);
  }

  changePage(page: number): void {
    this.currentPage = page;
  }

  getCategoryName(categoryId: string): string {
    const category = this.videoCategories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Sin categoría';
  }

  openVideoForm(video?: Video): void {
    if (video) {
      this.editMode = true;
      this.currentVideoId = video._id || null;
      this.videoForm = {
        title: video.title,
        description: video.description,
        thumbnailUrl: video.thumbnailUrl || '',
        videoUrl: video.videoUrl,
        category: video.category,
        featured: video.featured || false,
        price: video.price || 0,
        isFree: video.isFree || false,
        duration: video.duration || '',
        views: video.views || 0
      };
    } else {
      this.editMode = false;
      this.currentVideoId = null;
      this.videoForm = {
        title: '',
        description: '',
        thumbnailUrl: '',
        videoUrl: '',
        category: '',
        featured: false,
        price: 0,
        isFree: false,
        duration: '',
        views: 0
      };
    }
    this.showVideoForm = true;
  }

  closeVideoForm(): void {
    this.showVideoForm = false;
    this.editMode = false;
    this.currentVideoId = null;
  }

  saveVideo(): void {
    if (!this.videoForm.title || !this.videoForm.description || !this.videoForm.videoUrl || !this.videoForm.category) {
      Swal.fire({
        title: 'Error',
        text: 'Por favor completa todos los campos requeridos',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    this.spinner.show();
    const videoData: Video = {
      title: this.videoForm.title,
      description: this.videoForm.description,
      videoUrl: this.videoForm.videoUrl,
      category: this.videoForm.category,
      thumbnailUrl: this.videoForm.thumbnailUrl,
      duration: this.videoForm.duration,
      price: this.videoForm.price,
      isFree: this.videoForm.isFree,
      featured: this.videoForm.featured,
      views: this.videoForm.views
    };

    if (this.editMode && this.currentVideoId) {
      this.videosService.updateVideo(this.currentVideoId, videoData).subscribe({
        next: () => {
          this.spinner.hide();
          Swal.fire({
            title: 'Éxito',
            text: 'Video actualizado correctamente',
            icon: 'success',
            confirmButtonText: 'Ok'
          });
          this.closeVideoForm();
          this.loadVideos();
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al actualizar video', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo actualizar el video',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    } else {
      this.videosService.createVideo(videoData).subscribe({
        next: () => {
          this.spinner.hide();
          Swal.fire({
            title: 'Éxito',
            text: 'Video creado correctamente',
            icon: 'success',
            confirmButtonText: 'Ok'
          });
          this.closeVideoForm();
          this.loadVideos();
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al crear video', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo crear el video',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    }
  }

  deleteVideo(videoId: string): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.videosService.deleteVideo(videoId).subscribe({
          next: () => {
            this.spinner.hide();
            Swal.fire({
              title: 'Éxito',
              text: 'Video eliminado correctamente',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
            this.loadVideos();
          },
          error: (err: any) => {
            this.spinner.hide();
            console.error('Error al eliminar video', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo eliminar el video',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      }
    });
  }

  toggleVideoFeatured(videoId: string): void {
    const video = this.videos.find(v => v._id === videoId);
    if (video) {
      this.spinner.show();
      const updateData: Video = {
        title: video.title,
        description: video.description,
        videoUrl: video.videoUrl,
        category: video.category,
        thumbnailUrl: video.thumbnailUrl,
        duration: video.duration,
        price: video.price,
        isFree: video.isFree,
        featured: !video.featured,
        views: video.views
      };
      this.videosService.updateVideo(videoId, updateData).subscribe({
        next: () => {
          this.spinner.hide();
          this.loadVideos();
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al actualizar featured', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo actualizar el estado destacado',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    }
  }

  openVideoPreview(videoUrl: string): void {
    const videoId = this.extractVideoId(videoUrl);
    if (videoId) {
      this.previewVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}`);
      this.showVideoPreview = true;
    }
  }

  closeVideoPreview(): void {
    this.showVideoPreview = false;
    this.previewVideoUrl = null;
  }

  private extractVideoId(url: string): string | null {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  // Métodos de papers
  loadPapers(): void {
    this.spinner.show();
    this.papersService.getPapers().subscribe({
      next: (papers: Paper[]) => {
        console.log('Papers loaded from backend:', papers);
        // Asegurar que papers sea un array
        this.papers = Array.isArray(papers) ? papers : [];
        console.log('Papers after array check:', this.papers);
        
        // Log coverUrl for each paper
        this.papers.forEach((paper, index) => {
          console.log(`Paper ${index} coverUrl:`, paper.coverUrl);
        });
        
        this.applyPaperFilters();
        this.spinner.hide();
      },
      error: (err: any) => {
        console.error('Error al cargar papers', err);
        this.papers = []; // Inicializar como array vacío en caso de error
        this.filteredPapers = [];
        this.spinner.hide();
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los papers',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  loadActiveExperts(): void {
    console.log('🔍 Iniciando carga de expertos y mentores...');
    this.usersService.findAll().subscribe({
      next: (response: BaseResponse<User[]>) => {
        console.log('📡 Response completo:', response);
        // Extraer los usuarios del response
        const users = response.data || [];
        console.log('👥 Total usuarios recibidos:', users.length);
        
        // Log de algunos usuarios para verificar estructura
        if (users.length > 0) {
          console.log('👤 Primer usuario ejemplo:', users[0]);
          console.log('🔍 Tipos de usuario encontrados:', users.map(u => ({ id: u.type?.id, name: u.type?.name })));
        }
        
        // Filtrar expertos (tipo 4 = Experto) y mentores (tipo 2 = Coach) - temporalmente sin filtro de status
        this.activeExperts = users.filter(user => {
          const isExpert = user.type && user.type.id === 4;
          const isMentor = user.type && user.type.id === 2;
          const statusInfo = user.status ? `status: ${user.status.id} (${user.status.name})` : 'sin status';
          
          if (isExpert) {
            console.log(`🎯 Experto encontrado: ${user.firstName} ${user.lastName} - ${statusInfo}`);
          }
          if (isMentor) {
            console.log(`👨‍🏫 Mentor encontrado: ${user.firstName} ${user.lastName} - ${statusInfo}`);
          }
          
          // Temporalmente solo filtrar por tipo, no por status
          return isExpert || isMentor;
        });
        
        console.log('✅ Expertos y mentores activos finales:', this.activeExperts);
        console.log('📊 Cantidad de expertos y mentores activos:', this.activeExperts.length);
      },
      error: (err: any) => {
        console.error('❌ Error al cargar expertos y mentores:', err);
        this.activeExperts = [];
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los expertos y mentores',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  getExpertName(expertId: string): string {
    const expert = this.activeExperts.find(exp => exp._id === expertId);
    return expert ? `${expert.firstName} ${expert.lastName}` : expertId; // Fallback al ID si no se encuentra
  }

  applyPaperFilters(): void {
    // Asegurar que papers sea un array antes de usar filter
    if (!Array.isArray(this.papers)) {
      this.papers = [];
    }
    
    this.filteredPapers = this.papers.filter(paper => {
      const matchesSearch = paper.title.toLowerCase().includes(this.paperSearchTerm.toLowerCase()) ||
                           paper.description.toLowerCase().includes(this.paperSearchTerm.toLowerCase()) ||
                           paper.author.toLowerCase().includes(this.paperSearchTerm.toLowerCase());
      
      // Manejar el filtro de categorías: priorizar el nuevo campo categories, luego category
      let matchesCategory = true;
      if (this.selectedPaperCategory !== 'all') {
        const paperAny = paper as any;
        // Priorizar el nuevo campo categories
        if (paperAny.categories && Array.isArray(paperAny.categories) && paperAny.categories.length > 0) {
          matchesCategory = paperAny.categories.some((cat: any) => {
            const categoryId = typeof cat === 'object' && cat.id ? cat.id : cat;
            return categoryId === this.selectedPaperCategory;
          });
        } else if (Array.isArray(paper.category)) {
          // Si category es un array de objetos {id, name}, verificar si alguno coincide
          matchesCategory = paper.category.some((cat: any) => {
            const categoryId = typeof cat === 'object' && cat.id ? cat.id : cat;
            return categoryId === this.selectedPaperCategory;
          });
        } else if (typeof paper.category === 'string') {
          // Compatibilidad con formato antiguo (string separado por comas)
          matchesCategory = paper.category.includes(this.selectedPaperCategory);
        } else {
          matchesCategory = false;
        }
      }
      
      const matchesStatus = this.selectedPaperStatus === 'all' || paper.status?.toString() === this.selectedPaperStatus;
      return matchesSearch && matchesCategory && matchesStatus;
    });
    this.currentPaperPage = 1;
  }

  getPagePapers(): Paper[] {
    const startIndex = (this.currentPaperPage - 1) * this.paperPageSize;
    const endIndex = startIndex + this.paperPageSize;
    return this.filteredPapers.slice(startIndex, endIndex);
  }

  getTotalPaperPages(): number {
    return Math.ceil(this.filteredPapers.length / this.paperPageSize);
  }

  changePaperPage(page: number): void {
    this.currentPaperPage = page;
  }

  getPaperCategoryName(paper: any): string {
    // Priorizar el nuevo campo categories
    if (paper?.categories && Array.isArray(paper.categories) && paper.categories.length > 0) {
      const categoryNames = paper.categories.map((cat: any) => {
        if (typeof cat === 'object' && cat.name) {
          return cat.name;
        }
        if (typeof cat === 'object' && cat.id) {
          const foundCategory = this.paperCategories.find(c => c.id === cat.id);
          return foundCategory ? foundCategory.name : cat.id;
        }
        return '';
      }).filter((name: string) => name.length > 0);
      
      return categoryNames.length > 0 ? categoryNames.join(', ') : 'Sin categoría';
    }
    
    // Si no existe categories, usar category (formato antiguo para compatibilidad)
    const category = paper?.category;
    if (!category) return 'Sin categoría';
    
    // Si category es un array de objetos {id, name}
    if (Array.isArray(category)) {
      if (category.length === 0) return 'Sin categoría';
      
      const categoryNames = category.map((cat: any) => {
        if (typeof cat === 'object' && cat.name) {
          return cat.name;
        }
        if (typeof cat === 'object' && cat.id) {
          const foundCategory = this.paperCategories.find(c => c.id === cat.id);
          return foundCategory ? foundCategory.name : cat.id;
        }
        if (typeof cat === 'string') {
          const foundCategory = this.paperCategories.find(c => c.id === cat);
          return foundCategory ? foundCategory.name : cat;
        }
        return '';
      }).filter((name: string) => name.length > 0);
      
      return categoryNames.join(', ');
    }
    
    // Si category es un string (formato antiguo)
    if (typeof category === 'string') {
      // Si contiene comas, es múltiple categorías
      if (category.includes(',')) {
        const categoryIds = category.split(',').map(id => id.trim()).filter(id => id.length > 0);
        const categoryNames = categoryIds.map(id => {
          const foundCategory = this.paperCategories.find(cat => cat.id === id);
          return foundCategory ? foundCategory.name : id;
        });
        return categoryNames.join(', ');
      }
      
      // Categoría única
      const foundCategory = this.paperCategories.find(cat => cat.id === category);
      return foundCategory ? foundCategory.name : category;
    }
    
    return 'Sin categoría';
  }
  
  getPaperCategoriesArray(paper: any): Array<{id: string, name: string}> {
    // Priorizar el nuevo campo categories si existe
    if (paper?.categories && Array.isArray(paper.categories) && paper.categories.length > 0) {
      return paper.categories.map((cat: any) => {
        if (typeof cat === 'object' && cat.id && cat.name) {
          return { id: cat.id, name: cat.name };
        }
        if (typeof cat === 'object' && cat.id) {
          const foundCategory = this.paperCategories.find(c => c.id === cat.id);
          return { 
            id: cat.id, 
            name: foundCategory ? foundCategory.name : cat.id 
          };
        }
        return null;
      }).filter((cat: any) => cat !== null) as Array<{id: string, name: string}>;
    }
    
    // Si no existe categories, usar category (formato antiguo para compatibilidad)
    const category = paper?.category;
    if (!category) return [];
    
    // Si category es un array de objetos {id, name} (formato intermedio)
    if (Array.isArray(category)) {
      return category.map((cat: any) => {
        if (typeof cat === 'object' && cat.id && cat.name) {
          return { id: cat.id, name: cat.name };
        }
        if (typeof cat === 'object' && cat.id) {
          const foundCategory = this.paperCategories.find(c => c.id === cat.id);
          return { 
            id: cat.id, 
            name: foundCategory ? foundCategory.name : cat.id 
          };
        }
        if (typeof cat === 'string') {
          const foundCategory = this.paperCategories.find(c => c.id === cat);
          return { 
            id: cat, 
            name: foundCategory ? foundCategory.name : cat 
          };
        }
        return null;
      }).filter((cat: any) => cat !== null) as Array<{id: string, name: string}>;
    }
    
    // Si category es un string (formato antiguo)
    if (typeof category === 'string') {
      // Si contiene comas, es múltiple categorías
      if (category.includes(',')) {
        return category.split(',').map(id => id.trim()).filter(id => id.length > 0).map(id => {
          const foundCategory = this.paperCategories.find(cat => cat.id === id);
          return { 
            id: id, 
            name: foundCategory ? foundCategory.name : id 
          };
        });
      }
      
      // Categoría única
      const foundCategory = this.paperCategories.find(cat => cat.id === category);
      return [{ 
        id: category, 
        name: foundCategory ? foundCategory.name : category 
      }];
    }
    
    return [];
  }
  
  getCategoryNameById(categoryId: string): string {
    const category = this.paperCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  }
  
  toggleCategoryDropdown(): void {
    this.categoryDropdownOpen = !this.categoryDropdownOpen;
    this.categoryError = '';
  }
  
  isCategorySelected(categoryId: string): boolean {
    return this.paperForm.categories && this.paperForm.categories.includes(categoryId);
  }
  
  toggleCategory(categoryId: string): void {
    if (!this.paperForm.categories) {
      this.paperForm.categories = [];
    }
    
    const index = this.paperForm.categories.indexOf(categoryId);
    
    if (index > -1) {
      // Remover categoría
      this.paperForm.categories.splice(index, 1);
    } else {
      // Agregar categoría (validar máximo 4)
      if (this.paperForm.categories.length >= 4) {
        this.categoryError = 'Solo puedes seleccionar un máximo de 4 categorías';
        return;
      }
      this.paperForm.categories.push(categoryId);
      this.categoryError = '';
    }
    
    // Cerrar dropdown si se alcanza el máximo
    if (this.paperForm.categories.length >= 4) {
      this.categoryDropdownOpen = false;
    }
  }
  
  removeCategory(index: number): void {
    if (this.paperForm.categories && index >= 0 && index < this.paperForm.categories.length) {
      this.paperForm.categories.splice(index, 1);
      this.categoryError = '';
    }
  }
  
  parseCategoryString(category: any): string[] {
    if (!category) return [];
    
    // Si es un array de objetos {id, name}
    if (Array.isArray(category)) {
      return category.map((cat: any) => {
        if (typeof cat === 'object' && cat.id) {
          return cat.id;
        }
        return typeof cat === 'string' ? cat : '';
      }).filter((id: string) => id.length > 0);
    }
    
    // Si es un string separado por comas (formato antiguo para compatibilidad)
    if (typeof category === 'string') {
      return category.split(',').map(cat => cat.trim()).filter(cat => cat.length > 0);
    }
    
    return [];
  }

  getPaperStatus(paper: Paper): string {
    // Mapear el campo status numérico a texto
    if (!paper.status) {
      return 'Borrador'; // Por defecto si no tiene status
    }
    
    switch (paper.status) {
      case 1:
        return 'Activo';
      case 2:
        return 'Inactivo';
      case 3:
        return 'En revision';
      case 4:
        return 'Borrador';
      default:
        return 'Borrador';
    }
  }
  
  getPaperStatusClass(paper: Paper): string {
    // Retornar clase CSS según el estado
    if (!paper.status) {
      return 'draft';
    }
    
    switch (paper.status) {
      case 1:
        return 'active';
      case 2:
        return 'inactive';
      case 3:
        return 'review';
      case 4:
        return 'draft';
      default:
        return 'draft';
    }
  }

  openPaperForm(paper?: Paper): void {
    if (paper) {
      console.log('Paper data received:', paper);
      console.log('Tags type:', typeof paper.tags, 'Value:', paper.tags);
      console.log('LearningObjectives type:', typeof paper.learningObjectives, 'Value:', paper.learningObjectives);
      console.log('Keywords type:', typeof paper.keywords, 'Value:', paper.keywords);
      
      this.editPaperMode = true;
      this.currentPaperId = paper._id || null;
      // Parsear categorías: priorizar el nuevo campo categories, luego category (compatibilidad)
      const categories = (paper as any).categories && Array.isArray((paper as any).categories) && (paper as any).categories.length > 0
        ? this.parseCategoryString((paper as any).categories)
        : this.parseCategoryString(paper.category);
      this.paperForm = { 
        title: paper.title,
        description: paper.description,
        author: paper.author,
        category: paper.category || '', // Mantener para compatibilidad
        categories: categories,
        coverUrl: paper.coverUrl || '',
        fileUrl: paper.fileUrl,
        pages: paper.pages,
        price: paper.price,
        isFree: paper.isFree,
        featured: paper.featured,
        tags: this.parseArrayField(paper.tags),
        learningObjectives: this.parseArrayField(paper.learningObjectives, '\n'),
        publishedDate: paper.publishedDate ? new Date(paper.publishedDate).toISOString().split('T')[0] : '',
        downloads: paper.downloads,
        content: paper.content || '',
        summary: paper.summary || '',
        keywords: this.parseArrayField(paper.keywords),
        language: paper.language,
        isbn: paper.isbn || '',
        publisher: paper.publisher || '',
        year: paper.year,
        status: paper.status || 4
      };
    } else {
      this.editPaperMode = false;
      this.currentPaperId = null;
      this.selectedPdfFile = null;
      this.selectedCoverFile = null;
      this.paperForm = {
        title: '',
        description: '',
        author: '',
        category: '',
        categories: [],
        coverUrl: '',
        fileUrl: '',
        pages: 0,
        price: 0,
        isFree: false,
        featured: false,
        tags: '',
        learningObjectives: '',
        publishedDate: '',
        downloads: 0,
        content: '',
        summary: '',
        keywords: '',
        language: 'es',
        isbn: '',
        publisher: '',
        year: new Date().getFullYear(),
        status: 4 // Por defecto: Borrador
      };
      this.categoryDropdownOpen = false;
      this.categoryError = '';
    }
    this.showPaperForm = true;
  }

  closePaperForm(): void {
    this.showPaperForm = false;
    this.editPaperMode = false;
    this.currentPaperId = null;
    this.selectedPdfFile = null;
    this.selectedCoverFile = null;
  }

  onImageError(event: any): void {
    console.log('Image failed to load, setting default SVG');
    event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg4MFY4MEgyMFYyMFoiIGZpbGw9IiNEN0Q3RDciLz4KPHN2ZyB4PSIyNSIgeT0iMjUiIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNMTQgMkg2YTIgMiAwIDAgMC0yIDJ2MTZhMiAyIDAgMCAwIDIgMmgxMmEyIDIgMCAwIDAgMi0yVjh6IiBzdHJva2U9IiM5OTkiIHN0cm9rZS13aWR0aD0iMS41IiBmaWxsPSJub25lIi8+CjxwYXRoIGQ9Ik0xNCAyVjZoNiIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjEuNSIvPgo8cGF0aCBkPSJNMTYgMTNIOE0xNiAxN0g4TTEwIDlIOCIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjEuNSIvPgo8L3N2Zz4KPC9zdmc+Cg==';
  }

  onImageLoad(event: any): void {
    console.log('Image loaded successfully:', event.target.src);
  }

  getPaperCoverUrl(coverUrl: string | undefined): string {
    // Verificar si coverUrl está vacío, es null, undefined, o es un string vacío
    if (!coverUrl || coverUrl === '' || coverUrl === 'null' || coverUrl === 'undefined') {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg4MFY4MEgyMFYyMFoiIGZpbGw9IiNEN0Q3RDciLz4KPHN2ZyB4PSIyNSIgeT0iMjUiIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNMTQgMkg2YTIgMiAwIDAgMC0yIDJ2MTZhMiAyIDAgMCAwIDIgMmgxMmEyIDIgMCAwIDAgMi0yVjh6IiBzdHJva2U9IiM5OTkiIHN0cm9rZS13aWR0aD0iMS41IiBmaWxsPSJub25lIi8+CjxwYXRoIGQ9Ik0xNCAyVjZoNiIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjEuNSIvPgo8cGF0aCBkPSJNMTYgMTNIOE0xNiAxN0g4TTEwIDlIOCIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjEuNSIvPgo8L3N2Zz4KPC9zdmc+Cg==';
    }
    
    // Limpiar la URL de espacios extra
    const cleanUrl = coverUrl.trim();
    
    // Si la URL ya es completa (empieza con http), devolverla tal como está
    if (cleanUrl.startsWith('http')) {
      return cleanUrl;
    }
    
    // Verificar que environment.apiUrl esté definido
    if (!environment.apiUrl) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg4MFY4MEgyMFYyMFoiIGZpbGw9IiNEN0Q3RDciLz4KPHN2ZyB4PSIyNSIgeT0iMjUiIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNMTQgMkg2YTIgMiAwIDAgMC0yIDJ2MTZhMiAyIDAgMCAwIDIgMmgxMmEyIDIgMCAwIDAgMi0yVjh6IiBzdHJva2U9IiM5OTkiIHN0cm9rZS13aWR0aD0iMS41IiBmaWxsPSJub25lIi8+CjxwYXRoIGQ9Ik0xNCAyVjZoNiIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjEuNSIvPgo8cGF0aCBkPSJNMTYgMTNIOE0xNiAxN0g4TTEwIDlIOCIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjEuNSIvPgo8L3N2Zz4KPC9zdmc+Cg==';
    }
    
    // Si es una ruta relativa, construir la URL completa sin el prefijo /api
    const baseUrl = environment.apiUrlClean;
    return `${baseUrl}/${cleanUrl}`;
  }

  private parseArrayField(field: any, separator: string = ', '): string {
    if (!field) {
      return '';
    }
    
    // Si ya es un array, unirlo con el separador
    if (Array.isArray(field)) {
      return field.join(separator);
    }
    
    // Si es un string, intentar parsearlo como JSON
    if (typeof field === 'string') {
      // Limpiar el string de espacios extra
      const cleanField = field.trim();
      
      // Verificar si es un string JSON válido
      if (cleanField.startsWith('[') && cleanField.endsWith(']')) {
        try {
          // Intentar parsear como JSON
          let parsed = JSON.parse(cleanField);
          
          // Verificar si el resultado es un array
          if (Array.isArray(parsed)) {
            // Si el primer elemento es un string que parece JSON, intentar parsearlo también
            if (parsed.length > 0 && typeof parsed[0] === 'string' && 
                parsed[0].startsWith('[') && parsed[0].endsWith(']')) {
              try {
                const innerParsed = JSON.parse(parsed[0]);
                if (Array.isArray(innerParsed)) {
                  return innerParsed.join(separator);
                }
              } catch (innerError) {
                // Continuar con el parsing normal
              }
            }
            
            return parsed.join(separator);
          }
        } catch (error) {
          // Si el JSON parsing falla, intentar parsing manual
          try {
            // Remover los corchetes del inicio y final
            const content = cleanField.slice(1, -1);
            
            // Dividir por comas y limpiar cada elemento
            const items = content.split(',').map(item => {
              const trimmed = item.trim();
              // Remover comillas del inicio y final si existen
              if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
                  (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
                return trimmed.slice(1, -1);
              }
              return trimmed;
            }).filter(item => item.length > 0);
            
            return items.join(separator);
          } catch (manualError) {
            // Si todo falla, devolver el string original
          }
        }
      }
      
      // Si no es un array JSON, devolver el string tal como está
      return field;
    }
    
    // Si no es string ni array, devolver string vacío
    return '';
  }

  onPaperFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Verificar que sea un archivo PDF
      if (file.type !== 'application/pdf') {
        Swal.fire({
          title: 'Error',
          text: 'Por favor selecciona un archivo PDF válido',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        return;
      }
      
      this.selectedPdfFile = file;
      console.log('Archivo PDF seleccionado:', file.name);
    }
  }

  onPaperCoverSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Verificar que sea una imagen
      if (!file.type.startsWith('image/')) {
        Swal.fire({
          title: 'Error',
          text: 'Por favor selecciona un archivo de imagen válido',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        return;
      }
      
      this.selectedCoverFile = file;
      console.log('Imagen de portada seleccionada:', file.name);
      
      // Subir la imagen de portada al servidor
      this.spinner.show();
      this.papersService.uploadCover(file).subscribe({
        next: (response) => {
          this.spinner.hide();
          this.paperForm.coverUrl = response.coverUrl;
          console.log('Portada subida exitosamente:', response.coverUrl);
          console.log('paperForm.coverUrl después de subir:', this.paperForm.coverUrl);
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al subir portada:', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo subir la imagen de portada',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    }
  }

  savePaper(): void {
    // Validar que haya al menos una categoría seleccionada
    if (!this.paperForm.categories || this.paperForm.categories.length === 0) {
      this.categoryError = 'Debes seleccionar al menos una categoría';
      Swal.fire({
        title: 'Error',
        text: 'Por favor selecciona al menos una categoría',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }
    
    // Validar que no exceda el máximo de 4 categorías
    if (this.paperForm.categories.length > 4) {
      this.categoryError = 'Solo puedes seleccionar un máximo de 4 categorías';
      Swal.fire({
        title: 'Error',
        text: 'Solo puedes seleccionar un máximo de 4 categorías',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }
    
    if (!this.paperForm.title || !this.paperForm.description || !this.paperForm.author || this.paperForm.pages <= 0) {
      Swal.fire({
        title: 'Error',
        text: 'Por favor completa todos los campos requeridos',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    // Verificar que se haya seleccionado un archivo PDF para crear un nuevo paper
    if (!this.editPaperMode && !this.selectedPdfFile) {
      Swal.fire({
        title: 'Error',
        text: 'Para crear un paper necesitas subir un archivo PDF',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    // Verificar que se haya subido una imagen de portada
    if (!this.paperForm.coverUrl && !this.selectedCoverFile) {
      Swal.fire({
        title: 'Error',
        text: 'Para crear un paper necesitas subir una imagen de portada',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    // Si hay archivo seleccionado pero no se ha subido, subirlo primero
    if (this.selectedCoverFile && !this.paperForm.coverUrl) {
      Swal.fire({
        title: 'Procesando',
        text: 'Subiendo imagen de portada...',
        icon: 'info',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      this.papersService.uploadCover(this.selectedCoverFile).subscribe({
        next: (response) => {
          this.paperForm.coverUrl = response.coverUrl;
          console.log('Portada subida exitosamente:', response.coverUrl);
          Swal.close();
          this.continueWithSavePaper();
        },
        error: (err: any) => {
          console.error('Error al subir portada:', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo subir la imagen de portada',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
      return;
    }

    this.continueWithSavePaper();
  }

  private continueWithSavePaper(): void {
    this.spinner.show();
    
    // Procesar campos de arrays
    const tags = typeof this.paperForm.tags === 'string' 
      ? this.paperForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      : [];
    
    const learningObjectives = typeof this.paperForm.learningObjectives === 'string'
      ? this.paperForm.learningObjectives.split('\n').map(obj => obj.trim()).filter(obj => obj.length > 0)
      : [];
    
    const keywords = typeof this.paperForm.keywords === 'string'
      ? this.paperForm.keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword.length > 0)
      : [];
    
    // Procesar categorías: convertir array de IDs a array de objetos con id y name para el nuevo campo categories
    const categories = this.paperForm.categories && this.paperForm.categories.length > 0
      ? this.paperForm.categories.map(categoryId => {
          const category = this.paperCategories.find(cat => cat.id === categoryId);
          return {
            id: categoryId,
            name: category ? category.name : categoryId
          };
        })
      : [];
    
    // Mantener category como string (primera categoría o todas separadas por comas) para compatibilidad
    const categoryString = this.paperForm.categories && this.paperForm.categories.length > 0
      ? this.paperForm.categories.join(',')
      : '';
    
    console.log('Processed arrays:');
    console.log('Tags:', tags);
    console.log('LearningObjectives:', learningObjectives);
    console.log('Keywords:', keywords);
    console.log('Categories:', categories);
    console.log('Category string:', categoryString);
    
    console.log('paperForm.coverUrl antes de crear paperData:', this.paperForm.coverUrl);
    console.log('paperForm.coverUrl type:', typeof this.paperForm.coverUrl);
    console.log('paperForm.coverUrl length:', this.paperForm.coverUrl ? this.paperForm.coverUrl.length : 0);
    
    const paperData: CreatePaperDto = {
      title: this.paperForm.title,
      description: this.paperForm.description,
      author: this.paperForm.author,
      category: categoryString,
      categories: categories.length > 0 ? categories : undefined,
      coverUrl: this.paperForm.coverUrl || undefined,
      fileUrl: this.paperForm.fileUrl,
      pages: this.paperForm.pages,
      price: this.paperForm.price,
      isFree: this.paperForm.isFree,
      featured: this.paperForm.featured,
      tags: tags,
      learningObjectives: learningObjectives,
      publishedDate: this.paperForm.publishedDate ? new Date(this.paperForm.publishedDate).toISOString() : undefined,
      content: this.paperForm.content || undefined,
      summary: this.paperForm.summary || undefined,
      keywords: keywords,
      language: this.paperForm.language,
      isbn: this.paperForm.isbn || undefined,
      publisher: this.paperForm.publisher || undefined,
      year: this.paperForm.year,
      status: this.paperForm.status || 4 // Por defecto: Borrador
    };
    
    console.log('Final paperData:', paperData);
    console.log('coverUrl in paperData:', paperData.coverUrl);

    if (this.editPaperMode && this.currentPaperId) {
      // Actualizar paper existente
      this.papersService.updatePaper(this.currentPaperId, paperData).subscribe({
        next: () => {
          this.spinner.hide();
          Swal.fire({
            title: 'Éxito',
            text: 'Paper actualizado correctamente',
            icon: 'success',
            confirmButtonText: 'Ok'
          });
          this.closePaperForm();
          this.loadPapers();
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al actualizar paper', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo actualizar el paper',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    } else {
      // Crear nuevo paper
      if (this.selectedPdfFile) {
        this.papersService.createPaper(paperData, this.selectedPdfFile).subscribe({
          next: () => {
            this.spinner.hide();
            Swal.fire({
              title: 'Éxito',
              text: 'Paper creado correctamente',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
            this.closePaperForm();
            this.loadPapers();
          },
          error: (err: any) => {
            this.spinner.hide();
            console.error('Error al crear paper', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo crear el paper',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Para crear un paper necesitas subir un archivo PDF',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        this.spinner.hide();
      }
    }
  }

  deletePaper(paperId: string): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.papersService.deletePaper(paperId).subscribe({
          next: () => {
            this.spinner.hide();
            Swal.fire({
              title: 'Éxito',
              text: 'Paper eliminado correctamente',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
            this.loadPapers();
          },
          error: (err: any) => {
            this.spinner.hide();
            console.error('Error al eliminar paper', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo eliminar el paper',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      }
    });
  }

  togglePaperFeatured(paperId: string): void {
    this.spinner.show();
    this.papersService.toggleFeatured(paperId).subscribe({
      next: () => {
        this.spinner.hide();
        this.loadPapers();
      },
      error: (err: any) => {
        this.spinner.hide();
        console.error('Error al cambiar estado destacado', err);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo cambiar el estado destacado',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  openPaperPreview(paper: Paper): void {
    this.previewPaper = paper;
    this.showPaperPreview = true;
  }

  closePaperPreview(): void {
    this.showPaperPreview = false;
    this.previewPaper = null;
  }

  downloadPaper(paper: Paper | null): void {
    if (paper && paper._id) {
      this.spinner.show();
      this.papersService.downloadPaper(paper._id).subscribe({
        next: (blob: Blob) => {
          this.spinner.hide();
          // Crear URL del blob y descargar
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${paper.title}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
          
          Swal.fire({
            title: 'Descarga completada',
            text: `El paper "${paper.title}" se ha descargado exitosamente`,
            icon: 'success',
            confirmButtonText: 'Ok'
          });
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al descargar paper', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo descargar el paper',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    }
  }

  // ========== MÉTODOS PARA ACADEMY CATEGORIES ==========

  loadAcademyCategories(): void {
    this.spinner.show();
    this.configurationService.getDimensions().subscribe({
      next: (response) => {
        if (response && response.data) {
          this.academyCategories = response.data.map((dimension: any) => ({
            id: dimension.id,
            name: dimension.name,
            description: dimension.description || 'Sin descripción',
            status: 'active', // Las dimensiones no tienen estado, asumimos activo
            createdAt: new Date() // No tenemos fecha de creación, usamos la actual
          }));
        } else {
          this.academyCategories = [];
        }
        this.filteredAcademyCategories = [...this.academyCategories];
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Error al cargar dimensiones:', err);
        this.academyCategories = [];
        this.filteredAcademyCategories = [];
        this.spinner.hide();
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar las categorías de Academy',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  filterAcademyCategories(): void {
    const term = this.academyCategorySearchTerm.toLowerCase();
    this.filteredAcademyCategories = this.academyCategories.filter(category =>
      category.name.toLowerCase().includes(term) ||
      (category.description && category.description.toLowerCase().includes(term))
    );
  }

  openAcademyCategoryForm(): void {
    this.editAcademyCategoryMode = false;
    this.academyCategoryForm = {
      name: '',
      description: '',
      status: 'active'
    };
    this.showAcademyCategoryForm = true;
  }

  closeAcademyCategoryForm(): void {
    this.showAcademyCategoryForm = false;
  }

  editAcademyCategory(category: AcademyCategory): void {
    this.editAcademyCategoryMode = true;
    this.academyCategoryForm = {
      id: category.id,
      name: category.name,
      description: category.description,
      status: category.status
    };
    this.showAcademyCategoryForm = true;
  }

  saveAcademyCategory(): void {
    this.spinner.show();
    
    const categoryData = {
      name: this.academyCategoryForm.name,
      description: this.academyCategoryForm.description
    };

    const operation = this.editAcademyCategoryMode
      ? this.configurationService.updateDimension(this.academyCategoryForm.id!, categoryData)
      : this.configurationService.createDimension(categoryData);

    operation.subscribe({
      next: (response) => {
        this.spinner.hide();
        this.closeAcademyCategoryForm();
        this.loadAcademyCategories(); // Recargar la lista
        
        Swal.fire({
          title: 'Éxito',
          text: `Categoría ${this.editAcademyCategoryMode ? 'actualizada' : 'creada'} correctamente`,
          icon: 'success',
          confirmButtonText: 'Ok'
        });
      },
      error: (err) => {
        this.spinner.hide();
        console.error('Error al guardar categoría:', err);
        Swal.fire({
          title: 'Error',
          text: `No se pudo ${this.editAcademyCategoryMode ? 'actualizar' : 'crear'} la categoría`,
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  deleteAcademyCategory(categoryId: string): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.configurationService.deleteDimension(categoryId).subscribe({
          next: (response) => {
            this.spinner.hide();
            this.loadAcademyCategories(); // Recargar la lista
            
            Swal.fire({
              title: 'Eliminado',
              text: 'La categoría ha sido eliminada',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
          },
          error: (err) => {
            this.spinner.hide();
            console.error('Error al eliminar categoría:', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo eliminar la categoría',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      }
    });
  }

  // ========== MÉTODOS PARA EXPERT CATEGORIES ==========

  loadExpertCategories(): void {
    this.spinner.show();
    this.configurationService.getExpertCategories().subscribe({
      next: (response) => {
        if (response && response.data) {
          this.expertCategories = response.data.map((category: any) => ({
            id: category.id,
            name: category.name,
            description: category.description || 'Sin descripción',
            status: 'active', // Las categorías de expertos no tienen estado, asumimos activo
            createdAt: new Date() // No tenemos fecha de creación, usamos la actual
          }));
        } else {
          this.expertCategories = [];
        }
        this.filteredExpertCategories = [...this.expertCategories];
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Error al cargar categorías de expertos:', err);
        this.expertCategories = [];
        this.filteredExpertCategories = [];
        this.spinner.hide();
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar las categorías de expertos',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  filterExpertCategories(): void {
    const term = this.expertCategorySearchTerm.toLowerCase();
    this.filteredExpertCategories = this.expertCategories.filter(category =>
      category.name.toLowerCase().includes(term) ||
      (category.description && category.description.toLowerCase().includes(term))
    );
  }

  openExpertCategoryForm(): void {
    this.editExpertCategoryMode = false;
    this.expertCategoryForm = {
      name: '',
      description: '',
      status: 'active'
    };
    this.showExpertCategoryForm = true;
  }

  closeExpertCategoryForm(): void {
    this.showExpertCategoryForm = false;
  }

  editExpertCategory(category: ExpertCategory): void {
    this.editExpertCategoryMode = true;
    this.expertCategoryForm = {
      id: category.id,
      name: category.name,
      description: category.description,
      status: category.status
    };
    this.showExpertCategoryForm = true;
  }

  saveExpertCategory(): void {
    this.spinner.show();
    
    const categoryData = {
      name: this.expertCategoryForm.name,
      description: this.expertCategoryForm.description
    };

    const operation = this.editExpertCategoryMode
      ? this.configurationService.updateExpertCategory(this.expertCategoryForm.id!, categoryData)
      : this.configurationService.createExpertCategory(categoryData);

    operation.subscribe({
      next: (response) => {
        this.spinner.hide();
        this.closeExpertCategoryForm();
        this.loadExpertCategories(); // Recargar la lista
        
        Swal.fire({
          title: 'Éxito',
          text: `Categoría ${this.editExpertCategoryMode ? 'actualizada' : 'creada'} correctamente`,
          icon: 'success',
          confirmButtonText: 'Ok'
        });
      },
      error: (err) => {
        this.spinner.hide();
        console.error('Error al guardar categoría:', err);
        Swal.fire({
          title: 'Error',
          text: `No se pudo ${this.editExpertCategoryMode ? 'actualizar' : 'crear'} la categoría`,
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  deleteExpertCategory(categoryId: string): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.configurationService.deleteExpertCategory(categoryId).subscribe({
          next: (response) => {
            this.spinner.hide();
            this.loadExpertCategories(); // Recargar la lista
            
            Swal.fire({
              title: 'Eliminado',
              text: 'La categoría ha sido eliminada',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
          },
          error: (err) => {
            this.spinner.hide();
            console.error('Error al eliminar categoría:', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo eliminar la categoría',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      }
    });
  }
} 